import { useEffect, useState } from "react";
import { X, Loader2, Check, AlertTriangle } from "lucide-react";
import PhotoUpload from "@/components/PhotoUpload";
import { api, apiError } from "@/lib/api";

/**
 * ComposeModal — a config-driven create form used across the portal for admins
 * to add Inspections, Sites, Assets, Handovers, Breakdowns, Hazards, PPFs, etc.
 *
 * Props:
 *   open, onClose, onCreated
 *   title
 *   endpoint: "/inspections"  (POST target)
 *   fields: [{name, label, type, placeholder, required, rows, options, min, max}]
 *     type: text | textarea | select | date | number | photos | files | checkbox
 *   initial: {}
 *   verb: "Create" | "Save"
 *   testid
 */
export default function ComposeModal({
  open, onClose, onCreated,
  title, endpoint, fields = [], initial = {},
  verb = "Create", testid = "compose-modal",
  method = "POST",
  topSlot = null,
}) {
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [asyncOpts, setAsyncOpts] = useState({}); // { fieldName: [{value,label}] }

  // Load any async option endpoints defined on select-async fields
  useEffect(() => {
    if (!open) return;
    fields.forEach((f) => {
      if (f.type === "select-async" && f.optionsEndpoint && !asyncOpts[f.name]) {
        api.get(f.optionsEndpoint).then((r) => {
          const arr = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.results || r.data?.data || [];
          const filtered = f.filter ? arr.filter(f.filter) : arr;
          const sorted = f.sort ? [...filtered].sort(f.sort) : filtered;
          const mapped = sorted.map((o) => ({
            value: String(o.id || o._id || o.value),
            label: f.optionLabel ? f.optionLabel(o) : (o.name || o.title || o.template_name || o.label || String(o.id).slice(0, 8)),
          }));
          setAsyncOpts((prev) => ({ ...prev, [f.name]: mapped }));
        }).catch(() => setAsyncOpts((prev) => ({ ...prev, [f.name]: [] })));
      }
    });
  }, [open, fields]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    for (const f of fields) {
      if (f.required && (form[f.name] == null || form[f.name] === "")) {
        setMsg({ type: "err", text: `${f.label} is required.` });
        return;
      }
    }
    setBusy(true); setMsg(null);
    try {
      // Normalise: convert photo objects to their data-URL arrays too
      const body = { ...form };
      for (const f of fields) {
        if (f.type === "photos" || f.type === "files") {
          const list = form[f.name] || [];
          body[f.name] = list;
          body[`${f.name}_data`] = list.map((a) => a.data || a);
        }
        if (f.type === "number") {
          body[f.name] = form[f.name] === "" || form[f.name] == null ? null : Number(form[f.name]);
        }
      }
      const { data } = method === "PATCH"
        ? await api.patch(endpoint, body)
        : await api.post(endpoint, body);
      setMsg({ type: "ok", text: `${title} created.` });
      onCreated?.(data);
      setTimeout(onClose, 400);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center md:justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <form
        onSubmit={submit}
        className="w-full md:max-w-3xl max-h-[92vh] panel-raised overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">{verb}</div>
            <div className="font-display font-black text-lg tracking-tight">{title}</div>
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        <div className="p-5 overflow-auto">
          {topSlot && (
            <div className="mb-4">
              {typeof topSlot === "function" ? topSlot({ form, setForm }) : topSlot}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => {
            // Wide fields — textareas, photo/file uploaders, checkboxes and any
            // field explicitly flagged `fullWidth` — span both columns so
            // they keep their natural full-bleed layout. All other simple
            // inputs sit two-per-row on desktop so the form doesn't force
            // supervisors to scroll to reach the Create button.
            const wide = f.fullWidth ?? (f.type === "textarea" || f.type === "photos" || f.type === "files" || f.type === "checkbox");
            return (
            <div key={f.name} className={`field ${wide ? "md:col-span-2" : ""}`}>
              <label htmlFor={f.name}>
                {/* Strip any trailing " *" the field.label may include so we
                    never render the double-asterisk ("Field * *") when
                    f.required also adds one. */}
                {String(f.label || "").replace(/\s*\*\s*$/, "")}
                {f.required && <span style={{ color: "var(--primary)" }}> *</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  id={f.name} className="textarea" rows={f.rows || 3}
                  placeholder={f.placeholder}
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`compose-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select
                  id={f.name} className="select"
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`compose-${f.name}`}
                >
                  <option value="">— Select —</option>
                  {(f.options || []).map((o) => (
                    typeof o === "string"
                      ? <option key={o} value={o}>{o}</option>
                      : <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === "select-addable" ? (
                <SelectAddable
                  f={f}
                  value={form[f.name] ?? ""}
                  onChange={(v) => set(f.name, v)}
                />
              ) : f.type === "select-async" ? (
                <select
                  id={f.name} className="select"
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`compose-${f.name}`}
                >
                  <option value="">{asyncOpts[f.name] ? "— Select —" : "Loading…"}</option>
                  {(asyncOpts[f.name] || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === "photos" || f.type === "files" ? (
                <PhotoUpload
                  value={form[f.name] || []}
                  onChange={(v) => set(f.name, v)}
                  max={f.max || 6}
                  accept={f.type === "files" ? "*/*" : "image/*"}
                  label={f.label}
                />
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form[f.name]}
                    onChange={(e) => set(f.name, e.target.checked)}
                    data-testid={`compose-${f.name}`}
                  />
                  <span className="text-sm">{f.hint || "Yes"}</span>
                </label>
              ) : (
                <input
                  id={f.name}
                  type={f.type || "text"}
                  className="input"
                  placeholder={f.placeholder}
                  min={f.min} max={f.max}
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`compose-${f.name}`}
                />
              )}
              {f.hint && f.type !== "checkbox" && (
                <div className="text-[10px] uppercase tracking-widest text-muted">{f.hint}</div>
              )}
            </div>
            );
          })}
          </div>

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start mt-4`}>
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {msg.text}
            </div>
          )}
        </div>

        <div className="p-5 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="compose-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {verb}</>}
          </button>
        </div>
      </form>
    </div>
  );
}


/**
 * SelectAddable — a dropdown that lets privileged users add new options
 * inline (persisted via a caller-provided `onAddOption` async function).
 * Used by the Assets page so Supervisor+ can add a new site-wide asset
 * type (e.g. "LV Trailer") without leaving the Register Asset form.
 *
 * Props (via `f`):
 *   options: string[] — current list to show
 *   canAdd: boolean   — whether to render the inline add UI
 *   onAddOption(newValue): async — persist the new option; must resolve
 *                                  after backend confirms (throws on error)
 *   addLabel: string  — CTA text (e.g. "+ Add asset type")
 *   name:     string  — field name (for data-testid)
 *   placeholder?: string — placeholder for the new-option input
 */
function SelectAddable({ f, value, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const commitAdd = async () => {
    const clean = draft.trim();
    if (!clean) return;
    setBusy(true); setErr("");
    try {
      await f.onAddOption?.(clean);
      // The parent has appended the new option to the options prop. Select it.
      onChange(clean);
      setDraft("");
      setAdding(false);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <select
        id={f.name} className="select"
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={`compose-${f.name}`}
      >
        <option value="">— Select —</option>
        {(f.options || []).map((o) => (
          typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {f.canAdd && !adding && (
        <button
          type="button"
          className="text-[11px] uppercase tracking-widest font-black"
          style={{ color: "var(--primary)" }}
          onClick={() => { setErr(""); setAdding(true); }}
          data-testid={`compose-${f.name}-add`}
        >
          {f.addLabel || "+ Add new"}
        </button>
      )}

      {adding && (
        <div
          className="p-3 rounded-lg space-y-2"
          style={{ background: "var(--surface-2)", border: "1px dashed var(--primary)" }}
          data-testid={`compose-${f.name}-add-panel`}
        >
          <div className="text-[10px] uppercase tracking-widest text-muted">
            {f.addLabel || "New option"}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              className="input"
              placeholder={f.placeholder || "e.g. LV Trailer"}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitAdd(); }
                if (e.key === "Escape") { setAdding(false); setDraft(""); setErr(""); }
              }}
              disabled={busy}
              data-testid={`compose-${f.name}-add-input`}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || !draft.trim()}
              onClick={commitAdd}
              data-testid={`compose-${f.name}-add-save`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Save</>}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => { setAdding(false); setDraft(""); setErr(""); }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {err && (
            <div className="text-[11px]" style={{ color: "var(--danger)" }} data-testid={`compose-${f.name}-add-err`}>
              {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
